import React, { useState, useEffect } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import { onError } from '../../../utils/error.util';
import { useAppContext } from '../../../utils/content.util';
import { getContractingItems } from '../../../services/contracting.service';
import { ContractingItem } from '../../../models/contracting.model';

const LIMIT = 25;
const CONTRACTING_STATUSES = ['pending', 'completed'];

function ContractingTable({ data }: { data: ContractingItem[] }) {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell> </TableCell>
            <TableCell>Created Date</TableCell>
            <TableCell>Customer Name</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Analyst Email</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row) => (
            <TableRow key={row.id}>
              <TableCell>
                <Tooltip title="View">
                  <ReactRouterLink to={`/admin-contracting/${row.id}`}>
                    <VisibilityIcon color="primary" fontSize="small" />
                  </ReactRouterLink>
                </Tooltip>
              </TableCell>
              <TableCell>
                {row.createdAt ? format(new Date(row.createdAt), 'MM-dd-yyyy') : '—'}
              </TableCell>
              <TableCell>{row.dealerName || row.dealerId || '—'}</TableCell>
              <TableCell sx={{ textTransform: 'capitalize' }}>{row.status}</TableCell>
              <TableCell>{row.analystEmail}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function ContractingContainer() {
  const { isAuthenticated } = useAppContext();

  const [items, setItems] = useState<ContractingItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastKey, setLastKey] = useState('');
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [currentCursor, setCurrentCursor] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [shouldReload, setShouldReload] = useState(false);

  async function onLoad(cursor = '') {
    if (!isAuthenticated) return;

    setItems([]);
    setIsLoading(true);
    try {
      const response = await getContractingItems({ lastKey: cursor, limit: LIMIT, status: selectedStatus });
      setItems(response.items);
      setLastKey(response.lastKey);
    } catch (e) {
      onError(e);
    }
    setIsLoading(false);
  }

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldReload) {
      onLoad();
      setShouldReload(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldReload]);

  const handleStatusChange = (event: any) => {
    setSelectedStatus(event.target.value);
  };

  const handleStatusFilter = () => {
    setCursorHistory([]);
    setCurrentCursor('');
    onLoad('');
  };

  const handleClearFilter = () => {
    setSelectedStatus('');
    setCursorHistory([]);
    setCurrentCursor('');
    setShouldReload(true);
  };

  const handleNext = () => {
    if (lastKey) {
      setCursorHistory([...cursorHistory, currentCursor]);
      setCurrentCursor(lastKey);
      onLoad(lastKey);
    }
  };

  const handlePrev = () => {
    if (cursorHistory.length > 0) {
      const newHistory = [...cursorHistory];
      const previousCursor = newHistory.pop() || '';
      setCursorHistory(newHistory);
      setCurrentCursor(previousCursor);
      onLoad(previousCursor);
    }
  };

  return (
    <React.Fragment>
      <Grid size={{ xs: 12 }}>
        <div>
          <h2 className="pb-2 mt-4 mb-3 border-bottom">Contracting</h2>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                value={selectedStatus}
                onChange={handleStatusChange}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                {CONTRACTING_STATUSES.map((status) => (
                  <MenuItem key={status} value={status} sx={{ textTransform: 'capitalize' }}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleStatusFilter}>
              Filter
            </Button>
            <Button onClick={handleClearFilter}>Clear</Button>
          </Box>

          {isLoading ? (
            <CircularProgressComponent />
          ) : (
            <ContractingTable data={items} />
          )}

          {!isLoading && items.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 10 }}>
              <Button onClick={handlePrev} disabled={cursorHistory.length === 0}>
                Previous
              </Button>
              <Button onClick={handleNext} disabled={!lastKey}>
                Next
              </Button>
            </Box>
          )}
        </div>
      </Grid>
    </React.Fragment>
  );
}
